// Family space PWA (PWA1 Part B). Paths, endpoints, tiles and French copy.

export const FAMILY_BASE_PATH = '/family/';
export const FAMILY_REVOKE_PATH = '/family/revoke';
export const FAMILY_ROUTE = Object.freeze({ REVOKE: 'revoke', PAYMENT_RETURN: 'payment-return' });
export const FAMILY_STUDENT_TAB_KEY = 'neoprof-family-student-tab';
// FAM3 R10: "Qui êtes-vous ?" answer — sessionStorage (once per app session) + localStorage (preselected next time).
export const FAMILY_ACTOR_SESSION_KEY = 'neoprof-family-actor';
export const FAMILY_ACTOR_LAST_KEY = 'neoprof-family-actor-last';
export const FAMILY_CARD_PAYMENT_METHOD = 'carte_en_ligne';   // backend CARD_PAYMENT_METHOD_LABEL
export const FAMILY_PAYMENT_POLL_MS = 3000;
export const FAMILY_PAYMENT_POLL_MAX = 10;
export const FAMILY_SW_URL = '/service-worker.js';
export const FAMILY_SW_SCOPE = '/family/';
export const FAMILY_MANIFEST_URL = '/family/manifest.webmanifest';
export const FAMILY_APPLE_ICON_URL = '/brand/icon-180.png';
export const FAMILY_THEME_COLOR = '#eef0fb';
export const FAMILY_SW_SKIP_WAITING = 'SKIP_WAITING';

export const FAMILY_API = Object.freeze({
  REDEEM: '/family-space/redeem',
  REFRESH: '/family-space/refresh',
  REFRESH_MODE: '/family-space/refresh-mode',
  IDENTITY_MODE: '/family-space/identity-mode',
  MEMO_DATE: '/family-space/memo-date',
  REVOKE: '/family-space/revoke',
  SESSION: '/family-space/session',
  BOOTSTRAP: '/family-space/bootstrap',
  LOGINS: '/family-space/logins',
  SHARE_CODE: '/family-space/share-code',
  DEVICES: '/family-space/devices',
  SIGNAL: (deviceId) => `/family-space/devices/${deviceId}/signal`,
  DEVICE_LOGINS: (deviceId) => `/family-space/devices/${deviceId}/logins`,
  HOME: '/family-space/home',
  TILES: '/family-space/tiles',
  MISSIONS: '/family-space/missions',
  MISSION_DONE: (lessonId) => `/family-space/missions/${lessonId}/done`,
  DATA_DELETION: '/family-space/data-deletion-request',
  LESSONS: '/family-space/lessons',
  AGENDA: '/family-space/agenda',
  PAYMENTS: '/family-space/payments',
  PROGRESS: '/family-space/progress',
  STATEMENTS: '/family-space/statements',
  YEARLY_INVOICE: '/family-space/invoices/yearly',
  ATTESTATION: '/family-space/documents/attestation',
  SCHEDULE_REQUESTS: '/family-space/schedule-requests',
  ICS_TOKEN: '/family-space/agenda/ics-token',
  ICS_FEED: '/family-space/agenda.ics',
  AI_PREVIEW: '/family-space/ai-requests/preview',
  AI_REQUESTS: '/family-space/ai-requests',
  // Teacher side (JWT)
  TEACHER_INVITES: '/family-space/invites',
  TEACHER_DEVICES: '/family-space/teacher/devices',
  TEACHER_REVOKE_DEVICE: (deviceId) => `/family-space/teacher/devices/${deviceId}/revoke`,
  TEACHER_REVOKE_ALL: (familyId) => `/family-space/teacher/families/${familyId}/revoke-all`,
  TEACHER_AI_REQUESTS: '/family-space/teacher/ai-requests',
  TEACHER_AI_RESOLVE: (requestId) => `/family-space/teacher/ai-requests/${requestId}/resolve`,
  TEACHER_PAYMENT_RULES: '/family-space/teacher/payment-rules',
  TEACHER_PAYMENT_MODES: '/family-space/teacher/payment-modes',
  // NOTIF1 T26: this family's feed (device auth)
  NOTIFICATIONS: '/family-space/notifications',
  NOTIFICATIONS_READ: '/family-space/notifications/read',
  TEACHER_AI_SENDS: '/family-space/teacher/ai-sends',
  // Card payment (Part C)
  STRIPE_CONNECT: '/stripe/connect',
  STRIPE_STATUS: '/stripe/status',
  CARD_CHECKOUT: '/family-space/card-checkout',
  CARD_STATUS: '/family-space/card-status',
  TEACHER_PAYMENT_LINKS: '/family-space/teacher/payment-links',
});

// FAM2 Missions list: `status` query param + group `status`/`source` values.
export const MISSION_STATUS_FILTER = Object.freeze({ TODO: 'todo', DONE: 'done', ALL: 'all' });
export const MISSION_SOURCE = Object.freeze({ STUDENT: 'student', TEACHER: 'teacher' });

export const STATEMENT_PRESET = Object.freeze({ LAST_LESSON: 'last_lesson', LAST_MONTH: 'last_month' });
export const FAMILY_PAYMENT_MODE = Object.freeze({ AI: 'avance_immediate', CARD: 'card' });
// Backend 409 codes of the direct avance immédiate send (FAM3-B R5/R6).
export const FAMILY_AI_CODE = Object.freeze({ IDENTITY: 'identity_required', CHILDREN: 'children_required' });
export const FAMILY_DOCUMENT_FILE = Object.freeze({
  LAST_MONTH: 'releve-mois-dernier.pdf',
  LAST_YEAR: 'facture-annee-derniere.pdf',
  ATTESTATION: 'attestation-fiscale-sap.pdf',
});
export const FINANCE_TAB = Object.freeze({ LESSONS: 'lessons', PAYMENTS: 'payments' });
// Backend AI_FAMILY_SHARE_RATE (avance immédiate: the family pays half).
export const FAMILY_AI_SHARE_PERCENT = 50;
// Backend history `limit` max: Payer lists every unpaid lesson, Agenda the past lessons.
export const FAMILY_HISTORY_MAX_LIMIT = 100;
export const FAMILY_UNPAID_STATUSES = Object.freeze(['not_paid', 'partially_paid']);
export const FAMILY_AGENDA_DATE_PARAM = 'date';   // /family/agenda?date=YYYY-MM-DD opens that week

export const SCHEDULE_REQUEST = Object.freeze({
  NEW: 'new_lesson', CANCEL: 'cancel', APPROVED: 'approved', REJECTED: 'rejected',
});

export const TEACHER_FAMILY_REQUESTS_PATH = '/familles/demandes';
export const FAMILY_NOTIFICATIONS_POLL_MS = 60000;
// FAM3-B R1: teacher bell item kinds (backend schedule_requests.kind + 'ai_sent').
export const FAMILY_NOTIFICATION_KIND = Object.freeze({ NEW: 'new_lesson', CANCEL: 'cancel', AI_SENT: 'ai_sent' });
export const FAMILY_PAYMENT_METHODS = Object.freeze(['card', 'avance_immediate']);
export const AI_REQUEST_STATUS = Object.freeze({ DONE: 'done', DISMISSED: 'dismissed' });

/** URS2: offered after creating a family / student → family fiche, « Espace famille » tab. */
export const FAMILY_SPACE_ACTION = 'Créer l’espace famille';

export const FAMILY_TEACHER_COPY = Object.freeze({
  tab: 'Espace famille',
  /** NOTIF1 T18 / N27: how a family can pay from its space. */
  paymentMode: {
    title: 'Paiement depuis l’espace famille',
    labels: { avance_immediate: 'Avance immédiate', card: 'Carte bancaire', none: 'Sans moyen de paiement' },
    offline: 'Espèces, virement et chèque : vous les enregistrez vous-même.',
    filter: 'Sans moyen de paiement',
    missing: 'Sans moyen de paiement en ligne',
  },
  invite: 'Inviter la famille',
  newInvite: 'Nouveau lien d’invitation',
  inviteHint: 'Le lien est valable 30 jours et pour un nombre limité d’appareils.',
  code: 'Code',
  link: 'Lien',
  copyLink: 'Copier le lien',
  linkCopied: 'Lien copié.',
  qrAlt: 'QR code d’invitation',
  activeInvites: 'Invitations actives',
  usesLeft: (left, max) => `${left} / ${max} utilisations restantes`,
  expires: (date) => `Expire le ${date}`,
  noInvite: 'Aucune invitation active.',
  devices: 'Appareils',
  noDevice: 'Aucun appareil connecté.',
  lastSeen: (date) => `Vu le ${date}`,
  neverSeen: 'Jamais utilisé',
  revoke: 'Révoquer',
  revokeAll: 'Tout révoquer',
  revokeAllTitle: 'Révoquer tous les appareils ?',
  revokeAllMessage: 'Les appareils et les invitations de cette famille seront désactivés.',
  cancel: 'Annuler',
  revoked: 'Accès révoqué.',
  error: 'Action impossible. Réessayez.',
  // Avance immédiate requests (D17b)
  aiTitle: 'Anciennes demandes d’avance immédiate',
  aiLead: 'Demandes faites avant l’envoi direct. Traitez-les avec le flux habituel.',
  aiSendsTitle: 'Avances immédiates envoyées par les familles',
  aiSendsLead: 'Envoyées directement à l’URSSAF ces 7 derniers jours. La famille valide sous 48 h.',
  aiSendsEmpty: 'Aucun envoi récent.',
  aiEmpty: 'Aucune demande en attente.',
  rulesTitle: 'Moyens de paiement des familles',
  rulesLead: 'Activés par défaut dès que vous les avez configurés. Bloquez-les pour toutes les familles ou pour certaines.',
  rulesAll: 'Toutes les familles',
  rulesExceptions: 'Exceptions par famille',
  rulesNoException: 'Aucune exception.',
  rulesFamily: 'Famille',
  rulesMethod: 'Moyen',
  rulesAdd: 'Ajouter l’exception',
  rulesRemove: 'Retirer',
  rulesAllow: 'Autoriser',
  rulesBlock: 'Bloquer',
  rulesAllowed: 'autorisé',
  rulesBlocked: 'bloqué',
  rulesSaved: 'Réglage enregistré.',
  methodLabel: { card: 'Carte bancaire', avance_immediate: 'Avance immédiate' },
  aiAmount: (amount) => `${amount} €`,
  aiLessons: (count) => `${count} cours`,
  aiProcess: 'Traiter',
  aiDismiss: 'Ignorer',
  aiDone: 'Demande traitée.',
  reject: 'Refuser',
  cardTitle: 'Paiement par carte',
  cardLead: 'Les familles paient sur votre propre compte Stripe. Les frais Stripe sont à votre charge.',
  cardConnect: 'Connecter Stripe',
  cardContinue: 'Terminer la configuration Stripe',
  cardActive: 'Paiement par carte actif.',
  cardPending: 'Compte Stripe en cours de vérification.',
});

// FAM3: who acts on this device (login log, payment actor). Not a permission.
export const FAMILY_ACTOR = Object.freeze({ PARENT: 'parent', STUDENT: 'student' });
// FAM3-B R8: Accueil card order per profile; the first card is the spotlight.
export const FAMILY_HOME_CARD = Object.freeze({
  MISSIONS: 'missions', PAYMENTS: 'payments', PROGRESS: 'progress', INVOICES: 'invoices',
});
export const FAMILY_HOME_CARD_ORDER = Object.freeze({
  [FAMILY_ACTOR.PARENT]: [FAMILY_HOME_CARD.PAYMENTS, FAMILY_HOME_CARD.INVOICES, FAMILY_HOME_CARD.MISSIONS, FAMILY_HOME_CARD.PROGRESS],
  [FAMILY_ACTOR.STUDENT]: [FAMILY_HOME_CARD.MISSIONS, FAMILY_HOME_CARD.PROGRESS, FAMILY_HOME_CARD.PAYMENTS, FAMILY_HOME_CARD.INVOICES],
});

// Part D: which third factor the backend asks (after first + last name).
export const IDENTITY_MODE = Object.freeze({
  BIRTHDAY: 'birthday', MEMO_DATE: 'memo_date', EMAIL: 'email', NONE: 'none',
});
export const IDENTITY_FIELD = Object.freeze({
  [IDENTITY_MODE.BIRTHDAY]: { key: 'birth_date', type: 'date' },
  [IDENTITY_MODE.MEMO_DATE]: { key: 'memo_date', type: 'date' },
  [IDENTITY_MODE.EMAIL]: { key: 'email', type: 'email' },
});

// Last `{session, home}` kept on the device: instant repeat opens; wiped on 401.
export const FAMILY_BOOTSTRAP_CACHE_KEY = 'neoprof_family_bootstrap_v1';

// Station tiles. Internal screens + the public boards the backend `tiles` allows (D15).
export const FAMILY_TILE = Object.freeze({
  MISSIONS: 'missions',
  INVOICES: 'factures',
  AGENDA: 'agenda',
  HISTORY: 'historique',
  PROGRESS: 'progres',
  SETTINGS: 'parametres',
  FINANCES: 'finances',
  LEARNING: 'apprentissage',
  TOOLS: 'outils',
  PAY: 'payer',
  NOTIFICATIONS: 'notifications',
});

/** N31: browser pop-up opt-in for the family space (per browser). */
export const FAMILY_BROWSER_OPT_IN_KEY = 'np_family_browser_notifications';

// FAM3 R3: > this many students → one `n élèves associés` chip.
export const FAMILY_STUDENT_CHIPS_MAX = 3;
// Chip abbreviation of the canonical class id the backend sends (class_levels.class_id); others as sent.
export const FAMILY_CLASS_ABBR = Object.freeze({ '2nde': '2de', '1ere': '1re', Terminale: 'Tle' });

// FAM3 R2: Accueil · Outils · Paramètres ('' = home). Pushed screens highlight Outils.
export const FAMILY_NAV_HOME = '';
export const FAMILY_NAV_MAIN = Object.freeze([FAMILY_NAV_HOME, FAMILY_TILE.TOOLS, FAMILY_TILE.SETTINGS]);
// Legacy routes highlight the screen that replaced them.
export const FAMILY_NAV_ALIAS = Object.freeze({
  [FAMILY_TILE.INVOICES]: FAMILY_TILE.FINANCES,
  [FAMILY_TILE.HISTORY]: FAMILY_TILE.FINANCES,
});

// Backend tile key (= carousel intent) → public board, opened in a new tab.
export const FAMILY_BOARD_HREFS = Object.freeze({
  calcul: '/calcul',
  scratch: '/calcul/scratch',
  brevet: '/examens/brevet',
  bac_premiere: '/examens/bac-premiere',
  bac_terminale: '/examens/bac-terminale',
});

export const INVITE_CODE_LENGTH = 8;

// FAM3 R10/R11 short parent name from the decoded `relationship`; other values → copy fallback.
export const FAMILY_PARENT_SHORT = Object.freeze({ father: 'Papa', mother: 'Maman' });

export const FAMILY_COPY = Object.freeze({
  loading: 'Chargement…',
  error: 'Une erreur est survenue. Réessayez.',
  retry: 'Réessayer',
  // Entry (unknown device)
  entryTitle: 'Espace famille',
  entryLead: 'Saisissez le code reçu de votre enseignant.',
  codeLabel: 'Code d’accès',
  codePlaceholder: 'ABCD2345',
  enter: 'Entrer',
  entering: 'Connexion…',
  codeInvalid: 'Code invalide ou expiré. Demandez un nouveau lien à votre enseignant.',
  // Refresh (device known, server-expired)
  refreshTitle: 'Confirmez votre identité',
  refreshLead: 'Cet appareil doit être reconfirmé. Saisissez l’identité du parent.',
  parentFirstName: 'Prénom du parent',
  parentLastName: 'Nom du parent',
  confirm: 'Confirmer',
  refreshFailed: 'Identité non reconnue. Après 5 essais, l’appareil est bloqué.',
  identityLabels: {
    birthday: 'Date de naissance du parent',
    memo_date: 'Votre date mémorable',
    email: 'Email du parent (utilisable une seule fois)',
  },
  identityNone: 'Demandez un nouveau lien à votre enseignant.',
  memoTitle: 'Choisissez une date dont vous vous souviendrez',
  memoLead: 'Elle remplacera l’email pour vos prochaines reconnexions. Gardez-la pour vous.',
  memoLabel: 'Date mémorable',
  memoConfirmLabel: 'Confirmez la date',
  memoMismatch: 'Les deux dates sont différentes.',
  memoSave: 'Enregistrer',
  memoFailed: 'Impossible d’enregistrer la date. Réessayez.',
  haveCode: 'J’ai un nouveau code',
  // Install (D8)
  install: 'Installer l’application',
  installIosTitle: 'Installer sur iPhone',
  installIosSteps: [
    'Touchez le bouton Partager de Safari.',
    'Choisissez « Sur l’écran d’accueil ».',
    'Ouvrez l’application installée et saisissez votre code.',
  ],
  installIosRecommended: 'Installer (recommandé)',
  continueInSafari: 'Continuer dans Safari',
  updateAvailable: 'Mise à jour disponible',
  update: 'Mettre à jour',
  // "Qui êtes-vous ?" (FAM3 R10)
  whoAreYou: 'Qui êtes-vous ?',
  parentShortFallback: 'Parent',
  // Home
  homeGreeting: (familyName) => `Famille ${familyName}`,
  settings: 'Paramètres',
  tiles: {
    missions: 'Missions',
    factures: 'Factures',
    agenda: 'Agenda',
    historique: 'Historique',
    progres: 'Progrès',
    parametres: 'Paramètres',
    finances: 'Finances',
    apprentissage: 'Apprentissage',
    outils: 'Outils',
    payer: 'Paiements',
    calcul: 'Station Calcul',
    scratch: 'Scratch',
    brevet: 'Brevet',
    bac_premiere: 'Bac 1re',
    bac_terminale: 'Bac Tle',
  },
  tileDescriptions: {
    missions: 'Le travail donné à chaque cours.',
    factures: 'Relevés, factures et paiement.',
    agenda: 'Les prochains cours.',
    historique: 'Cours passés et paiements.',
    progres: 'Notes et évaluations.',
    parametres: 'Appareils, partage, données.',
    finances: 'Cours, paiements et documents.',
    apprentissage: 'Calcul, Scratch, Brevet, Bac.',
    calcul: 'S’entraîner au calcul.',
    scratch: 'Programmer avec Scratch.',
    brevet: 'Sujets du brevet.',
    bac_premiere: 'Sujets du bac de première.',
    bac_terminale: 'Sujets du bac de terminale.',
  },
  nav: {
    label: 'Navigation principale',
    home: 'Accueil',
    close: 'Fermer',
  },
  /** NOTIF1 T26: séances / paiements enregistrés. */
  notifications: {
    title: 'Notifications',
    empty: 'Aucune notification pour le moment.',
    new: 'Nouveau',
    bell: (unread) => (unread ? `Notifications, ${unread} non lue${unread > 1 ? 's' : ''}` : 'Notifications'),
    browser: {
      toggle: 'Notifications du navigateur',
      hint: 'Une alerte quand votre professeur enregistre une séance ou un paiement, tant que l’espace famille est ouvert.',
      denied: 'Notifications bloquées par le navigateur. Autorisez-les dans les réglages du site, puis rechargez la page.',
      unsupported: 'Ce navigateur ne gère pas les notifications.',
    },
  },
  learningEmpty: 'Aucun espace d’entraînement ouvert pour le moment.',
  // Accueil cards (FAM3 R3–R9): one title + one value per card.
  studentChip: (firstName, classAbbr) => (classAbbr ? `${firstName} · ${classAbbr}` : firstName),
  studentsAssociated: (n) => `${n} élèves associés`,
  cards: { next: 'Prochain cours', missions: 'Missions', payments: 'Paiements', progress: 'Progrès', invoices: 'Factures' },
  cardWithStudent: (title, firstName) => `${title} · ${firstName}`,
  hour: (h, m) => (m === '00' ? `${Number(h)} h` : `${Number(h)} h ${m}`),
  learningLabel: (board, names) => `${board} — ${names}`,
  nextLessonLine: (parts) => parts.filter(Boolean).join(' · '),
  noNextLesson: 'Aucun cours prévu',
  missionsNone: 'Aucune mission au dernier cours',
  missionOf: (day) => `Mission du ${day}`,
  missionDoneTag: 'Faite',
  missionTodoTag: 'À faire',
  missionNone: 'Pas de mission',
  missionLine: (count, day) => `${count} · cours du ${day}`,
  missionTodo: (count) => `${count} à faire`,
  staleTag: 'Ancienne',
  nothingDue: 'Rien à régler',
  amountDueShort: (amount) => `${amount} à régler`,   // amount already formatted ("60,00 €")
  progressEmpty: 'Pas encore de note',
  progressAverage: (value) => `${value} / 20`,
  progressMissions: (pct) => `${pct} %`,
  progressFamily: (value) => `Moyenne ${value} / 20`,
  progressImproving: (n, counted) => `${n} en progrès sur ${counted}`,
  statementOf: (month) => `Relevé de ${month}`,
  invoicesValue: 'Relevés et historique',
  learningPick: 'Pour qui ?',
  learningCaptions: { calcul: 'Calcul', scratch: 'Scratch', brevet: 'Brevet', bac_premiere: 'Bac 1re', bac_terminale: 'Bac Tle' },
  // Revoke page
  revokeTitle: 'Révoquer cet appareil',
  revokeLead: 'L’appareil ne pourra plus ouvrir l’espace famille.',
  revokeConfirm: 'Révoquer',
  revokeDone: 'Appareil révoqué.',
  revokeFailed: 'Lien invalide ou déjà utilisé.',
});

// Step 17 screens.
export const FAMILY_SCREEN_COPY = Object.freeze({
  loadMore: 'Afficher la suite',
  back: 'Retour',
  empty: 'Rien pour le moment.',
  studentTabs: 'Élèves',
  // Missions (FAM2.14)
  search: 'Rechercher',
  searchLabel: 'Rechercher une mission',
  filter: 'Filtrer',
  filterTitle: 'Filtrer les missions',
  filterSubject: 'Matière',
  allSubjects: 'Toutes',
  filterFrom: 'Du',
  filterTo: 'Au',
  apply: 'Appliquer',
  reset: 'Effacer',
  missionStatus: 'Statut des missions',
  missionTabs: { all: (n) => `Toutes · ${n}`, todo: (n) => `À faire · ${n}`, done: (n) => `Faites · ${n}` },
  missionStatusBadge: { todo: 'À faire', done: 'Faite' },
  missionTabLabels: { all: 'Toutes', todo: 'À faire', done: 'Faites' },
  missionDue: (day) => `Pour ${day}`,
  missionGiven: (day) => `Donnée ${day}`,
  exerciseCount: (n) => `${n} exercice${n > 1 ? 's' : ''}`,
  markDone: 'Marquer comme faite',
  markTodo: 'Marquer comme à faire',
  markedDone: 'Faite',
  markedDoneLabel: 'Faite — marquer comme à faire',
  // Mark done confirmation (owner ruling 2026-10-04): client-side only, nothing stored.
  missionWho: 'Qui es-tu ?',
  missionWhoParent: 'Parent',
  missionConfirmSelf: 'Tu confirmes avoir terminé toutes les missions ?',
  missionConfirmOther: (name) => `Tu confirmes que ${name} a terminé toutes les missions et qu’il t’a autorisé à cocher pour lui ?`,
  missionConfirmYes: 'Oui, je confirme',
  missionConfirmCancel: 'Annuler',
  verified: 'Vérifiée en cours',
  openMission: (subject, day) => `Ouvrir la mission ${subject} du ${day}`,
  missionsEmptyTitle: 'Station dégagée',
  missionsEmpty: {
    todo: (name) => `Aucune mission à faire pour ${name}.`,
    done: (name) => `Aucune mission faite pour ${name}.`,
    all: (name) => `Aucune mission pour ${name}.`,
  },
  // Finances (FAM2.16)
  dueLabel: 'À régler',
  pay: 'Payer',
  ledger: 'Historique',
  documents: 'Documents',
  statementOf: (month) => `Relevé de ${month}`,
  invoiceOf: (year) => `Facture ${year}`,
  attestationOf: (year) => `Attestation fiscale ${year}`,
  noLessonsIn: (period) => `Aucun cours en ${period}`,
  documentsEmpty: 'Aucun document pour le moment.',
  historySearch: 'Rechercher : mois, montant ou matière',
  historySearchPayments: 'Rechercher : mois ou montant',
  allStudents: 'Tous',
  paidBy: (label) => `Fait par ${label}`,
  paidByKind: { teacher: 'Professeur', parent: 'Parent' },
  statementHint: 'Le relevé n’est pas une facture. La facture annuelle en est une.',
  pdfFailed: 'Document indisponible. Réessayez plus tard.',
  dailyLimit: 'Limite quotidienne atteinte. Réessayez demain.',
  checkoutFailed: 'Paiement par carte indisponible.',
  // PAY1: the family picks the lessons to pay, oldest first.
  pickLessons: 'Séances à régler',
  pickLessonsHint: 'Les séances se règlent dans l’ordre : la plus ancienne d’abord.',
  cardTitle: 'Paiement par carte',
  payAmount: (amount) => `Payer ${amount}`,
  cardNothing: 'Aucune séance à régler par carte.',
  paymentMethod: { carte_en_ligne: 'Carte en ligne' },
  aiTitle: 'Avance immédiate',
  aiLead: (amount, count) => `${amount} € pour ${count} cours. La demande part directement à l’URSSAF ; vous la validez sur votre espace URSSAF.`,
  aiNothing: 'Aucun cours à déclarer pour le moment.',
  aiPending: 'Une demande est déjà en attente chez votre enseignant.',
  aiConfirm: 'Envoyer à l’URSSAF',
  aiLeft: (n) => `${n} demande${n > 1 ? 's' : ''} possible${n > 1 ? 's' : ''} aujourd’hui`,
  aiSent: 'Envoyée à l’URSSAF — validez-la sous 48 h sur votre espace URSSAF.',
  aiInProgress: 'Un paiement en cours',
  aiConfirmationTitle: 'Confirmez votre paiement par email',
  aiConfirmationMessage: 'Vous allez recevoir un email de l’URSSAF. Ouvrez-le et confirmez la demande de paiement sur votre espace URSSAF sous 48 heures.',
  aiUnderstood: 'J’ai compris',
  aiFailed: 'Demande refusée. Vérifiez les informations du parent.',
  aiSecurityNotice: 'Deuxième demande aujourd’hui : par mesure de sécurité, nous vérifions l’identité du parent avant de l’envoyer.',
  aiSecurityContinue: 'Continuer',
  aiChildrenLead: 'Cette année, l’avance immédiate est plafonnée à 1 500 € par enfant.',
  aiChildrenLabel: 'Combien d’enfants avez-vous ?',
  aiChildrenMax: 20,
  // Payer + R11 widget (FAM3.12)
  aiShare: (percent) => `Avance immédiate : vous payez ${percent} %`,
  dueLessons: 'Cours à régler',
  payerIsParent: 'Vous êtes le parent ?',
  payerAuthorized: (parent) => `Avez-vous été autorisé à faire ce paiement par ${parent} ?`,
  payerWho: 'Qui paie ?',
  parentAuthorFallback: 'votre parent',
  yes: 'Oui',
  no: 'Non',
  // Payment return (Part C)
  paymentTitle: 'Paiement',
  paymentPending: 'Paiement en cours de confirmation…',
  paymentConfirmed: (amount) => `Paiement de ${amount} € confirmé. Merci !`,
  paymentSlow: 'La confirmation prend plus de temps que prévu. Elle apparaîtra dans l’historique.',
  backToFinances: 'Retour aux finances',
  // Agenda (D13, D13b)
  agendaSection: { upcoming: 'À venir', past: 'Passés' },
  agendaEmpty: { upcoming: 'Aucun cours prévu.', past: 'Aucun cours passé.' },
  lessonLocation: 'Domicile',
  addToCalendar: 'Ajouter à mon agenda',
  agendaStatus: {
    confirmed: 'Confirmé',
    change: 'Modification demandée',
    cancelled: 'Annulé',
  },
  lessonLine: (student, subject) => (subject ? `${student} — ${subject}` : student),
  cancelRequest: 'Demander l’annulation',
  cancelConfirm: 'Confirmer la demande',
  cancelSent: 'Demande d’annulation envoyée. Le cours reste prévu tant que l’enseignant ne l’a pas annulé.',
  requestLesson: 'Demander un cours',
  requestStudent: 'Élève',
  requestMessage: 'Message',
  requestSlots: 'Créneaux souhaités (facultatif)',
  requestSend: 'Envoyer la demande',
  requestSent: 'Demande envoyée à votre enseignant.',
  copyFeed: 'Copier le lien du calendrier',
  feedCopied: 'Lien du calendrier copié.',
  androidCalendarHint: 'Ouvrez le fichier téléchargé avec votre agenda pour importer les séances. Pour une mise à jour automatique, copiez le lien du calendrier.',
  actionFailed: 'Action impossible. Réessayez.',
  // Historique (D14)
  lessons: 'Cours',
  payments: 'Paiements',
  lessonsEmpty: 'Aucun cours passé.',
  paymentsEmpty: 'Aucun paiement.',
  hours: (h) => `${h} h`,
  euros: (amount) => `${Number(amount || 0).toFixed(2)} €`,
  lessonStatus: { completed: 'Fait', cancelled: 'Annulé', scheduled: 'Prévu' },
  paymentStatus: { paid: 'Payé', not_paid: 'À régler', partially_paid: 'Partiel', pending: 'En attente' },
  // DEL1 R2/R4: payment no longer counted (cancelled by the teacher, refunded, or rejected by URSSAF).
  paymentVoided: { cancelled: 'Annulé', rejected: 'Annulé' },
  // Progrès
  progressEmpty: 'Aucune évaluation pour le moment.',
  grade: (grade, outOf) => `${grade} / ${outOf}`,
  averageTitle: 'Moyenne générale',
  onTwenty: (value) => `${value} / 20`,
  chartLabel: (points) => `Moyenne par mois : ${points}`,
  chartPoint: (month, value) => `${month} ${value}`,
  subjectsTitle: 'Par matière',
  gradesTitle: 'Notes',
  missionsTitle: 'Missions',
  missionsLast: 'Dernière fois',
  missionsLastValue: { true: 'faites ✓', false: 'non faites ✗' },
  missionsLastMonth: 'Le mois dernier',
  missionsTotal: 'Depuis le début',
  missionsDifficulty: 'Difficulté',
  percent: (n) => `${n} %`,
  streak: (n) => `${n} semaine${n > 1 ? 's' : ''} consécutive${n > 1 ? 's' : ''}`,
  noValue: '—',
  // Paramètres (FAM3.15)
  installApp: 'Télécharger comme une app',
  active: 'Actif',
  deviceDetails: 'Appareil',
  deviceAdded: 'Ajouté le',
  deviceLastSeen: 'Dernière activité',
  addDevice: '+ Ajouter un appareil',
  deletionRequest: 'Demander la suppression de nos données',
  deletionTitle: 'Supprimer vos données ?',
  deletionMessage: 'Votre enseignant reçoit la demande par email. Il vérifie avec vous, puis supprime les données.',
  deletionConfirm: 'Envoyer la demande',
  deletionCancel: 'Annuler',
  deletionSent: 'Demande envoyée à votre enseignant.',
  devices: 'Appareils',
  devicesEmpty: 'Aucun appareil.',
  profileTitle: 'Profil de cet appareil',
  profileLead: 'L’accueil met en avant ce qui compte pour ce profil.',
  thisDevice: 'Cet appareil',
  loginHistory: 'Connexions',
  loginsEmpty: 'Aucune connexion.',
  loginLine: (who, when) => `${who} · ${when}`,
  neverSeen: 'Jamais utilisé',
  signal: 'Signaler',
  signalSent: 'Votre enseignant a été prévenu.',
  share: 'Ajouter un appareil',
  shareLead: 'Scannez ce code avec le nouvel appareil. Valable 15 minutes, une seule fois.',
  shareAlt: 'QR code pour ajouter un appareil',
  shareExpires: (time) => `Expire à ${time}`,
});

